import {
    useState,
    FC
} from "react";
import {
    useParams,
    useHistory
} from "react-router-dom";
import { useRecoilState } from "recoil";
import {
    useMutation,
    useQuery,
    UseQueryOptions,
    QueryKey
} from "react-query";

// Material UI imports
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Switch,
    Tooltip,
    Typography,
    Alert
} from "@mui/material";

import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { Property } from "../../models";
import PropertyDAO from "../../lib/property/dao/PropertyDAO";
import DataStorePropertyDAOFactory from "../../lib/property/dao/datastore/DataStorePropertyDAOFactory";
import { propertyIdToPropertySelectorFamily } from "../../lib/property/state/PropertyRecoilState";
import { staticDashboardService } from "../../lib/dashboard/StaticDashboardService";
import globalStyle from "../../assets/style/globalStyle";
import { useSnackbar } from "notistack";

interface RouteParams {
    propertyId: string;
}

interface DashboardInfo {
    isGenerated: boolean;
    fullUrl: string | null | undefined;
    lastGenerated?: string;
}

const propertyDAO: PropertyDAO = DataStorePropertyDAOFactory.getInstance();

/**
 * CodeBuildJobList Component
 * Displays and manages a property's issue dashboard
 */
export const CodeBuildJobList: FC = () => {
    const { propertyId } = useParams<RouteParams>();
    const history = useHistory();

    const [property, setProperty] = useRecoilState<Property | null>(
        propertyIdToPropertySelectorFamily(propertyId)
    );

    const [showCopySuccess, setShowCopySuccess] = useState<boolean>(false);

    const globalClasses = globalStyle();
    const snackbar = useSnackbar();

    const {
        data: dashboardInfo,
        refetch: refetchDashboardInfo,
        isLoading: isDashboardInfoLoading
    } = useQuery<DashboardInfo, Error>({
        queryKey: ["dashboardInfo", propertyId],
        queryFn: async () => {
            const staticDashboardInformation = await staticDashboardService.get(propertyId);
            const baseUrl = window.location.origin;
            const fullUrl = `${baseUrl}/${staticDashboardInformation.metadata.url}`
            return {
                isGenerated: staticDashboardInformation.metadata.isGenerated,
                fullUrl: fullUrl,
                lastGenerated: staticDashboardInformation.metadata.lastModified
            };
        }
    } as UseQueryOptions<DashboardInfo, Error, DashboardInfo, QueryKey>);

    // Dashboard creation mutation
    const {
        mutate,
        status: triggerBuildStatus,
        reset: resetBuildStatus
    } = useMutation({
        mutationFn: async () => {
            // Create the dashboard
            await staticDashboardService.create(propertyId);

            // Refresh dashboard info
            await refetchDashboardInfo();

            snackbar.enqueueSnackbar("Dashboard successfully generated! You can now access it via the URL above",
                { variant: "success", autoHideDuration: 3000 }
            );
        }
    });

    /**
     * Navigate to the dashboard view
     */
    const openDashboard = (): void => {
        history.push(`/public/dashboard/property/${propertyId}`);
    };

    /**
     * Copy dashboard URL to clipboard
     */
    const copyDashboardUrl = (): void => {
        const url = dashboardInfo?.fullUrl ?? '';

        navigator.clipboard.writeText(url).then(() => {
            setShowCopySuccess(true);
            snackbar.enqueueSnackbar("Dashboard URL copied to clipboard!", { variant: "success", autoHideDuration: 2000 });
            setShowCopySuccess(false);
        });
    };

    /**
     * Handle auto rebuild toggle
     */
    const handleAutoRebuildToggle = async (checked: boolean): Promise<void> => {
        if (!property) return;

        await propertyDAO.editProperty(propertyId, {
            ...property,
            autoDashboardRebuild: checked
        });

        setProperty(prev => prev ? { ...prev, autoDashboardRebuild: checked } : prev);
    };

    /**
     * Handle dashboard build/rebuild
     */
    const handleBuildDashboard = (): void => {
        resetBuildStatus();
        mutate();
    };

    const isLoading = triggerBuildStatus === "loading";
    const isDashboardGenerated = Boolean(dashboardInfo?.isGenerated);

    return (
        <Box display="flex" flexDirection="column">
            <Typography variant="h6">Issue Dashboard</Typography>

            <Card className={globalClasses.listContainerWithBorder} sx={{ mt: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    <Box display="flex" flexDirection="column" gap={1}>
                        {isDashboardGenerated ? (
                            // Dashboard URL section
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                p={2}
                                bgcolor="rgba(0, 0, 0, 0.03)"
                                borderRadius={1}
                            >
                                <Typography variant="body1" fontWeight="500">
                                    Dashboard URL:
                                </Typography>

                                <a
                                    href={dashboardInfo?.fullUrl ?? '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        color: "inherit",
                                        textDecoration: "none"
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            color: "primary.main",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        {dashboardInfo?.fullUrl ?? "No URL available"}
                                    </Typography>
                                </a>

                                <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                                    Generated: {dashboardInfo?.lastGenerated}
                                </Typography>

                                <Tooltip title={showCopySuccess ? "Copied!" : "Copy URL"} placement="top">
                                    <IconButton
                                        onClick={copyDashboardUrl}
                                        color="primary"
                                        size="small"
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <IconButton
                                    onClick={openDashboard}
                                    color="primary"
                                    size="small"
                                >
                                    <LaunchIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ) : (
                            // No dashboard yet message
                            <Box
                                display="flex"
                                alignItems="center"
                                p={2}
                                bgcolor="rgba(0, 0, 0, 0.03)"
                                borderRadius={1}
                                color="text.secondary"
                            >
                                <InfoOutlinedIcon sx={{ mr: 1, color: 'info.main' }} />
                                <Typography>
                                    No dashboard has been generated yet. Click the "Create Issue Dashboard Site" button to generate one.
                                </Typography>
                            </Box>
                        )}

                        {/* Controls section */}
                        <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={property?.autoDashboardRebuild ?? false}
                                        onChange={(_, checked) => handleAutoRebuildToggle(checked)}
                                    />
                                )}
                                label="Auto Rebuild"
                            />

                            <Tooltip
                                title={isLoading ? "Build in progress..." : ""}
                                enterTouchDelay={0}
                            >
                                <span>
                                    <Button
                                        onClick={handleBuildDashboard}
                                        disabled={isLoading}
                                        variant="contained"
                                        color="primary"
                                        sx={{
                                            minWidth: "220px",
                                            position: "relative"
                                        }}
                                    >
                                        {isDashboardGenerated ? "Rebuild" : "Create"} Issue Dashboard Site
                                        {isLoading && (
                                            <CircularProgress
                                                size={24}
                                                sx={{
                                                    position: "absolute",
                                                    right: 10
                                                }}
                                                color="inherit"
                                            />
                                        )}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};